import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FileText, PenLine } from 'lucide-react';
import { getOpleiderContract, tekenOpleiderContract, foutMelding, OpleiderOnboardingState } from '../../services/opleiderOnboardingApi';
import { Kaart, StapKop, Veld, Knop, Melding } from './ui';

export const StapContract: React.FC<{ state: OpleiderOnboardingState; onKlaar: () => void }> = ({ state, onKlaar }) => {
  const { t } = useTranslation('pages');
  const { data: contract, isLoading } = useQuery({ queryKey: ['opleider-contract'], queryFn: getOpleiderContract, staleTime: Infinity });
  const [voortgang, setVoortgang] = useState(0);
  const [gelezen, setGelezen] = useState(false);
  const [akkoord, setAkkoord] = useState(false);
  const [naam, setNaam] = useState('');
  const viewer = useRef<HTMLDivElement>(null);

  const opScroll = useCallback(() => {
    const el = viewer.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    const pct = max <= 0 ? 1 : el.scrollTop / max;
    setVoortgang(Math.min(1, pct));
    if (pct >= 0.98) setGelezen(true);
  }, []);

  const teken = useMutation({
    mutationFn: () =>
      tekenOpleiderContract({ getypte_naam: naam, matrix_akkoord: akkoord, volledig_gelezen: gelezen, contract_hash: contract!.hash }),
    onSuccess: onKlaar,
  });

  // Zelfde normalisatie als de server (hoofdletters, spaties en accenten tellen niet).
  const norm = (v: string) => v.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
  const naamKlopt = norm(naam) === norm(state.provider.contactpersoon);

  return (
    <Kaart>
      <StapKop
        nummer={4}
        titel={t('opleiderWizard.contract.titel', { defaultValue: 'Overeenkomst & juridische handtekening' })}
        intro={t('opleiderWizard.contract.intro', {
          defaultValue: 'Lees de Gebruikersovereenkomst BeLagom Platform volledig door. Hierin zijn het financiële model (Stripe-split 5/10/85 en tariefpariteit), de AVG-afspraken rond cursistgegevens en het platform-omzeilingsverbod vastgelegd.',
        })}
      />

      <div className="overflow-hidden rounded-xl border border-white/10">
        <div className="flex items-center gap-2 border-b border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm font-semibold text-slate-200">
          <FileText className="h-4 w-4 text-sky-400" aria-hidden="true" />
          <span className="truncate">{contract?.titel ?? '…'}</span>
          <span className="ml-auto shrink-0 font-mono text-[11px] text-slate-500">{contract?.versie}</span>
        </div>
        <div className="h-1 bg-white/5" aria-hidden="true">
          <div className="h-full bg-sky-400 transition-[width]" style={{ width: `${Math.round(voortgang * 100)}%` }} />
        </div>
        <div
          ref={viewer}
          onScroll={opScroll}
          tabIndex={0}
          role="document"
          aria-label={t('opleiderWizard.contract.viewer', { defaultValue: 'Tekst van de overeenkomst' })}
          className="prose prose-sm prose-invert max-h-96 max-w-none overflow-y-auto bg-slate-950/60 px-5 py-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-400 prose-h3:mb-1 prose-h3:mt-5 prose-h3:text-sm prose-h3:text-white prose-p:my-2 prose-p:text-slate-300 prose-strong:text-sky-300 prose-ul:my-1 prose-li:my-0.5 prose-li:text-slate-300"
        >
          {isLoading || !contract ? (
            <p className="text-slate-500">{t('opleiderWizard.contract.laden', { defaultValue: 'Overeenkomst laden…' })}</p>
          ) : (
            // Server-side opgebouwd uit vaste tekst met HTML-escaping.
            <div dangerouslySetInnerHTML={{ __html: contract.html }} />
          )}
        </div>
      </div>
      {!gelezen && (
        <p className="mt-2 text-xs text-slate-500">{t('opleiderWizard.contract.scrollHint', { defaultValue: 'Scroll door de volledige overeenkomst om verder te kunnen.' })}</p>
      )}

      <label
        className={`mt-6 flex gap-3 rounded-xl border p-4 text-sm leading-relaxed transition ${
          !gelezen ? 'cursor-not-allowed border-white/5 text-slate-600' : akkoord ? 'cursor-pointer border-emerald-400/50 bg-emerald-400/10 text-emerald-100' : 'cursor-pointer border-amber-400/40 bg-amber-400/5 text-slate-200'
        }`}
      >
        <input
          type="checkbox"
          className="mt-0.5 h-5 w-5 shrink-0 accent-emerald-400"
          disabled={!gelezen}
          checked={akkoord}
          onChange={(e) => setAkkoord(e.target.checked)}
        />
        <span>“{contract?.matrix_verklaring}”</span>
      </label>

      <div className="mt-6">
        <Veld
          name="getypte_naam"
          autoComplete="off"
          label={t('opleiderWizard.contract.naam', { defaultValue: 'Typ uw naam ter verificatie' })}
          icoon={<PenLine className="h-4 w-4" />}
          value={naam}
          onChange={(e) => setNaam(e.target.value)}
          placeholder={state.provider.contactpersoon}
          hint={t('opleiderWizard.contract.naamHint', { defaultValue: 'Exact zoals opgegeven als hoofdcontactpersoon: {{naam}}', naam: state.provider.contactpersoon })}
          disabled={!gelezen}
          className="font-serif"
        />
      </div>

      {teken.isError && (
        <div className="mt-5">
          <Melding>{foutMelding(teken.error, t('opleiderWizard.contract.fout', { defaultValue: 'Ondertekenen mislukt.' }))}</Melding>
        </div>
      )}

      <div className="mt-6">
        <Knop groot className="w-full" bezig={teken.isPending} disabled={!gelezen || !akkoord || !naamKlopt || !contract} onClick={() => teken.mutate()}>
          ✍️ {t('opleiderWizard.contract.submit', { defaultValue: 'Bevestig & Onderteken Overeenkomst Digitaal' })}
        </Knop>
        <p className="mt-2 text-center text-[11px] text-slate-500">
          {t('opleiderWizard.contract.bewijs', { defaultValue: 'Versie, inhoudshash, tijdstip en IP-adres worden als bewijs van ondertekening vastgelegd.' })}
        </p>
      </div>
    </Kaart>
  );
};
